import { loadEmailSettingsFromData } from '$lib/stores/emailSettings';
import { loadSmsPacksFromData, loadSmsSettingsFromData } from '$lib/stores/smsSettings';
import { widgetConfig } from '$lib/widget/stores';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { loadScansHistoryFromApi } from '../qr-code/qr-code';
import { activityStore } from './activity';
import { loadAgendaSettingsFromData } from './agenda';
import { apiStore } from './api';
import { areaStore } from './area';
import { loadContractFromData } from './billing';
import { loadBlockingPeriodsFromData } from './blocking-period';
import { clearBookingStore, loadBookings } from './booking';
import { businessStore } from './business';
import { disableChat } from './chat';
import { loadCustomFieldsFromData } from './custom-field';
import { featureFlipStore } from './feature-flip';
import { loadFeaturesClickedFromData, loadFeaturesVotesFromData } from './features';
import { loadBookableGiftcardsFromData, loadGiftcardStatsFromData } from './giftcard';
import { notificationSettingStore } from './notification-setting';
import { loadOfferCollectionsFromData, offerStore } from './offer';
import { openingHourStore } from './opening-hour';
import { loadExceptionsFromData } from './opening-hour-exception';
import { sessionStore } from './session';
import { clearSessionInstanceStore, loadSessionInstance } from './session-instance';
import { initSocketWatchers } from './socket-watchers';
import { loadTagsFromData } from './tags';
import { reloadThemeColor } from './theme';
import { reloadWidget } from './widget';

let lastRefresh: DateTime = DateTime.now();

export function refreshAppData(force: boolean = false) {
	// If the last refresh was more than 10 seconds ago, refresh the data
	if (force || DateTime.now().diff(lastRefresh).as('seconds') > 10) {
		refresh();
	}
}

function refresh() {
	lastRefresh = DateTime.now();
	appDataLoaded.set(false);
	get(apiStore)
		?.get('/business/all')
		.then((res) => {
			if (res.data?.business === undefined) {
				window.location.href = '/';
			}
			businessStore.refreshFromData(res.data.business);
			activityStore.refreshFromData(res.data.activities);
			areaStore.refreshFromData(res.data.areas);
			sessionStore.refreshFromData(res.data.sessions);
			offerStore.refreshFromData(res.data.offers);
			loadOfferCollectionsFromData(res.data.offerCollections);
			openingHourStore.refreshFromData(res.data.openingHours);
			loadContractFromData(res.data.contract);
			loadTagsFromData(res.data.tags);
			loadAgendaSettingsFromData(res.data.agendaSettings);
			featureFlipStore.refreshFromData(res.data.features);
			notificationSettingStore.refreshFromData(res.data.notificationSettings);
			loadExceptionsFromData(res.data.openingHourExceptions);
			loadBlockingPeriodsFromData(res.data.blockingPeriods);
			loadBookableGiftcardsFromData(res.data.bookableGiftcards);
			loadGiftcardStatsFromData(res.data.giftcardStats);
			loadCustomFieldsFromData(res.data.customFields);
			loadEmailSettingsFromData(res.data.emailSettings);
			loadSmsSettingsFromData(res.data.smsSettings);
			loadSmsPacksFromData(res.data.smsPacks);
			loadFeaturesVotesFromData(res.data.featuresVotes);
			loadFeaturesClickedFromData(res.data.featuresClicked);

			disableChat();
			appDataLoaded.set(true);

			clearBookingStore();
			loadBookings();
			clearSessionInstanceStore();
			loadSessionInstance();
			loadScansHistoryFromApi(res.data.scans);

			widgetConfig.set(res.data.widgetConfig);

			reloadThemeColor(); // Refresh theme colors
			reloadWidget(); // Refresh widget

			initSocketWatchers();
		});
}

export const appDataLoaded = writable(false);
